import { useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import type { Title } from "@/types";
import { typeMetaMap } from "@/lib/titles";

interface HeroCarouselProps {
  slides: Title[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % slides.length);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [paused, slides.length]);

  if (slides.length === 0) return null;

  const goTo = (next: number) => setIndex((next + slides.length) % slides.length);

  const handleTouchStart = (event: TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      if (delta < 0) goTo(index + 1);
      else goTo(index - 1);
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative h-[440px] w-full overflow-hidden bg-primary-950 md:h-[560px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="焦点图轮播"
    >
      {slides.map((slide, slideIndex) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            slideIndex === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={slide.backdrop}
            alt={`${slide.name} 主题图`}
            title={`${slide.name} - WW我的快乐在哪里影视资讯`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20" />
        </div>
      ))}

      <div className="relative z-10 flex h-full w-full items-center">
        <div className="mx-auto w-full max-w-page px-4 md:px-6">
          <h1 className="sr-only">WW我的快乐在哪里 - 最新影视资讯与剧情介绍</h1>
          <div key={slides[index].id} className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-primary-950">
              <i className="ri-fire-line" aria-hidden="true" />
              热门推荐
            </span>
            <h2 className="mt-4 text-3xl font-black leading-tight text-background-50 md:text-5xl">
              {slides[index].name}
            </h2>
            <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-background-200">
              <span>{typeMetaMap[slides[index].type].label}</span>
              <span className="text-accent-400">·</span>
              <span>{slides[index].year}</span>
              <span className="text-accent-400">·</span>
              <span>{slides[index].region}</span>
              <span className="text-accent-400">·</span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-star-fill text-accent-400" aria-hidden="true" />
                {slides[index].rating.toFixed(1)}
              </span>
            </p>
            <p className="mt-4 hidden max-w-xl text-base leading-relaxed text-background-200 sm:block">
              {slides[index].short}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${slides[index].id}`}
                className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400 whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-base" aria-hidden="true" />
                查看剧情详情
              </Link>
              <Link
                to={`/category/${slides[index].type}`}
                className="inline-flex items-center gap-2 rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 whitespace-nowrap"
              >
                浏览更多{typeMetaMap[slides[index].type].label}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => goTo(index - 1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 transition-colors hover:bg-accent-500 hover:text-primary-950 md:left-6 md:flex"
      >
        <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => goTo(index + 1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 transition-colors hover:bg-accent-500 hover:text-primary-950 md:right-6 md:flex"
      >
        <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true" />
      </button>

      <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {slides.map((slide, slideIndex) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(slideIndex)}
            aria-label={`切换到 ${slide.name}`}
            className={`h-1.5 rounded-full transition-all ${
              slideIndex === index ? "w-7 bg-accent-500" : "w-1.5 bg-background-50/60 hover:bg-background-50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}